import { Box, Grid2 } from '@mui/material';
import React, { useMemo, useState } from 'react';
import CustomTable from '@ui-components/Table/CustomTable';
import CustomTableBody from '@ui-components/Table/CustomTableBody';
import CustomTableCell from '@ui-components/Table/CustomTableCell';
import CustomTableHead from '@ui-components/Table/CustomTableHead';
import CustomTableRow from '@ui-components/Table/CustomTableRow';
import CustomTooltip from '@ui-components/ToolTip/CustomToolTip';
import CustomPagination from '@ui-components/Table/CustomPagination';

//Icons
import Delete from '@assets/images/delete-withoutbox.svg';
import Download from '@assets/images/download.svg';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import {
  useDeleteDocument,
  useDownloadDocument,
  useGetSessionDocuments,
} from '../../API/hooks/ConsultMoreDetailsHook';
import { type SuccessResponse } from '../../API/services/ConsultMoreDetailsService';
import ConfirmationPopup from '../components/ConfirmationPopup';
import CustomSnackbar from '@qler-view/shared_lib/src/shared/lib/ui-components/Snackbar/CustomSnackbar';

interface SessionDocProps {
  consultId: number;
}

const SessionDoc: React.FC<SessionDocProps> = ({ consultId }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;
  const userId = Number(localStorage.getItem('userId') ?? '0');
  const [confirmationPopup, setConfirmationPopup] = useState(false);
  const { SessionDocumentList, refetch } = useGetSessionDocuments(
    userId,
    consultId
  );
  const { mutate: mutateDownloadSessionDocument } = useDownloadDocument();
  const { mutate: mutateDeleteSessionDocument } = useDeleteDocument();
  const [sessionDocId, setSessionDocId] = useState<string>('');
  const [openToast, setOpenToast] = useState(false);
  const [message, setMessage] = useState('');

  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return SessionDocumentList?.slice(startIndex, startIndex + rowsPerPage);
  }, [currentPage, rowsPerPage, SessionDocumentList]);

  const PastDocumentsLength = SessionDocumentList?.length ?? 0;

  // Function to handle page change
  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };
  const handleDownloadDocument = (documentId: number) => {
    mutateDownloadSessionDocument(
      {
        documentId,
      },
      {
        onSuccess: (data: string) => {
          const a = document.createElement('a');
          a.href = data;
          document.body.appendChild(a);
          a.click();
          a.remove();
          globalThis.URL.revokeObjectURL(data);
        },
      }
    );
  };
  const handleDeleteDocument = (documentId: number) => {
    mutateDeleteSessionDocument(
      {
        documentId,
      },
      {
        onSuccess: (data: SuccessResponse) => {
          console.log(data);
          refetch().then(() => {
            setMessage('Document deleted successfully.');
            setOpenToast(true);
            // Check if the current page is empty after deletion
            if (
              paginatedRows &&
              paginatedRows?.length === 1 &&
              currentPage > 1
            ) {
              setCurrentPage(currentPage - 1);
            }
          });
        },
      }
    );
  };
  return (
    <Box className="mt-16">
      <Box>
        {openToast && (
          <CustomSnackbar
            id="toast-consult-list"
            successOrError={'error'}
            open={openToast}
            handleClose={() => {
              setOpenToast(!openToast);
            }}
            toastrMessage={message}
          />
        )}
      </Box>
      <Box>
        <ConfirmationPopup
          openPopup={confirmationPopup}
          onClosePopup={() => {
            setConfirmationPopup(false);
          }}
          title={'Confirmation Action'}
          message={
            ' Are you sure you want to delete this document? This action is irreversible.'
          }
          onConfirm={() => {
            setConfirmationPopup(false);
            handleDeleteDocument(Number(sessionDocId));
          }}
        />
      </Box>
      <Grid2 container>
        <Grid2 size={{ xs: 12 }}>
          {paginatedRows && paginatedRows?.length > 0 ? (
            <>
              <CustomTable id="pastSession-table">
                <CustomTableHead id="pastSession-table-head">
                  <CustomTableRow id="pastSession-table-row">
                    <CustomTableCell id="pastSession-table-cell-1">
                      File name
                    </CustomTableCell>
                    <CustomTableCell
                      id="pastSession-table-cell-2"
                      align="right"
                    >
                      File Size
                    </CustomTableCell>
                    <CustomTableCell
                      id="pastSession-table-cell-3"
                      align="right"
                    >
                      Uploaded On
                    </CustomTableCell>
                    <CustomTableCell
                      id="pastSession-table-cell-4"
                      align="right"
                    >
                      Uploaded By
                    </CustomTableCell>

                    <CustomTableCell
                      id="pastSession-table-cell-6"
                      align="right"
                    >
                      Actions
                    </CustomTableCell>
                  </CustomTableRow>
                </CustomTableHead>
                <CustomTableBody id="pastSession-table-body">
                  {paginatedRows?.map((row) => (
                    <CustomTableRow
                      key={row.consultId}
                      id="pastSession-table-cell-1"
                    >
                      <CustomTableCell
                        id="pastSession-table-cell-2"
                        className="ellipsis-cell"
                      >
                        <CustomTooltip
                          id="pastSession-table-tooltip"
                          title={row.fileName}
                          placement="top"
                        >
                          <>{row.fileName ? row.fileName : 'N/A'}</>
                        </CustomTooltip>
                      </CustomTableCell>
                      <CustomTableCell
                        id="pastSession-table-cell-3"
                        align="right"
                      >
                        {row.fileSize ? row.fileSize : 'N/A'}
                      </CustomTableCell>
                      <CustomTableCell
                        id="pastSession-table-cell-4"
                        align="right"
                      >
                        {row.uploadedOn ? row.uploadedOn : 'N/A'}
                      </CustomTableCell>
                      <CustomTableCell
                        id="pastSession-table-cell-5"
                        align="right"
                      >
                        {row.uploadedBy ? row.uploadedBy : 'N/A'}
                      </CustomTableCell>

                      <CustomTableCell
                        id="pastSession-table-cell-6"
                        align="right"
                      >
                        <Box className="flex">
                          <Box className="mr-12">
                            <CustomIconButton
                              id="SessionDocIcon-delete-button"
                              icon={<Delete />}
                              onClick={() => {
                                setSessionDocId(row.documentId.toString());
                                setConfirmationPopup(true);
                              }}
                            ></CustomIconButton>
                          </Box>
                          <Box className="justify-center">
                            <CustomIconButton
                              id="SessionDocDownload-button"
                              icon={<Download />}
                              onClick={() =>
                                handleDownloadDocument(row.documentId)
                              }
                            ></CustomIconButton>
                          </Box>
                        </Box>
                      </CustomTableCell>
                    </CustomTableRow>
                  ))}
                </CustomTableBody>
              </CustomTable>
              <Box display="flex" justifyContent="center" mt={2}>
                <CustomPagination
                  id="session-pagination"
                  count={Math.ceil(PastDocumentsLength / rowsPerPage)} // Calculate the total number of pages
                  page={currentPage}
                  onPageChange={handlePageChange}
                  color="primary"
                  siblingCount={1}
                  boundaryCount={1}
                  showFirstButton={true}
                  showLastButton={true}
                  size="small"
                />
              </Box>
            </>
          ) : (
            <Grid2
              size={{ xs: 12, sm: 12, md: 6, lg: 4 }}
              className="button-width"
            >
              <Box className="text-align-center no-consults-data">
                <CustomTypography
                  variant="body2"
                  color="textDisabled"
                  id="noAcceptedConsults"
                >
                  No documents available
                </CustomTypography>
              </Box>
            </Grid2>
          )}
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default SessionDoc;
